import { useCallback } from 'react';

import { Currency } from '@/api/rest/resources/types/currency';
import { StewardedValue } from '@/api/rest/resources/types/organization';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { getDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { useUserContext } from '@/lib/auth';

export type CurrencyData = {
  currencyCode: string;
  conversionRate?: number;
};

export interface CurrencyHook {
  /** Current organization's currency data */
  currency: CurrencyData;

  /** Currency code from organization */
  currencyCode: string;

  /** Conversion rate from organization */
  conversionRate?: number;

  /** Convert Euro amount to user's currency and format it using Intl.NumberFormat */
  convertAndFormat: (euroAmount: number, options?: Intl.NumberFormatOptions) => string;

  /** Format amount in current currency using Intl.NumberFormat */
  formatAmount: (amount: number, options?: Intl.NumberFormatOptions) => string;

  /** Format StewardedValue object (for organization summary values) */
  formatStewardedValue: (stewardedValue: StewardedValue, options?: Intl.NumberFormatOptions) => string;

  /** Check if organization has a non-EUR currency configured */
  isNonEurCurrency: boolean;

  /** Check if conversion rate is available */
  hasConversionRate: boolean;
}

export const useCurrency = (): CurrencyHook => {
  const { user } = useUserContext();

  const currencyCode = useDefaultCurrency();

  // Extract currency data from organization
  const conversionRate = user?.membership?.organization?.conversion_rate;

  const currency: CurrencyData = {
    currencyCode,
    conversionRate,
  };

  // Check if we have a non-EUR currency
  const isNonEurCurrency = currencyCode !== Currency.EUR;

  // Check if conversion rate is available when needed
  const hasConversionRate = !isNonEurCurrency || (typeof conversionRate === 'number' && conversionRate > 0);

  // Convert and format Euro amount to user's currency using enhanced display currency
  const convertAndFormat = useCallback(
    (euroAmount: number, options?: Intl.NumberFormatOptions): string => {
      const euroValue = Number(euroAmount);
      if (Number.isNaN(euroValue)) return getDisplayCurrency(0, window.navigator.language, currencyCode, options);

      // Apply conversion if needed
      const convertedAmount = isNonEurCurrency && conversionRate ? euroValue * conversionRate : euroValue;

      return getDisplayCurrency(convertedAmount, window.navigator.language, currencyCode, options);
    },
    [currencyCode, conversionRate, isNonEurCurrency],
  );

  // Format amount in current currency (no conversion)
  const formatAmount = useCallback(
    (amount: number, options?: Intl.NumberFormatOptions): string => {
      return getDisplayCurrency(amount, window.navigator.language, currencyCode, options);
    },
    [currencyCode],
  );

  // Format StewardedValue object - handles EUR values from organization summary API
  const formatStewardedValue = useCallback(
    (stewardedValue: StewardedValue, options?: Intl.NumberFormatOptions): string => {
      // StewardedValue always comes with EUR from backend, so use convertAndFormat
      return convertAndFormat(stewardedValue.value, options);
    },
    [convertAndFormat],
  );

  return {
    currency,
    currencyCode,
    conversionRate,
    convertAndFormat,
    formatAmount,
    formatStewardedValue,
    isNonEurCurrency,
    hasConversionRate,
  };
};
